<template>
    <!-- `fit` must not be combined with an explicit height: its `height: 100% !important` would win -->
    <q-scroll-area
        class="scroll-container"
        :class="{ fit: maxHeight === undefined }"
        :style="maxHeight === undefined ? undefined : { height: `${scrollAreaHeight}px` }"
        ref="scrollAreaRef"
    >
        <q-infinite-scroll @load="fireLoadMore" ref="infiniteScrollRef">
            <div>
                <slot />
            </div>
            <template #loading>
                <div class="row loading justify-center q-pa-md">
                    <q-spinner-ios size="2em" />
                    <div class="q-pl-sm">{{ loadMoreText }}</div>
                </div>
            </template>
        </q-infinite-scroll>
    </q-scroll-area>
</template>
<!--
Currently the loading template in the q-infinite-scroll leads to an error in the unit test with vitest,
therefore this is not used until further notice. This problem may be solved with a quasar update.
At the time of the error quasar ^2.15.4 was used.
The current workaround is to stub the q-spinner-ios eg. activities.spec.vitest

Problem with QInfiniteScroll + filter changes:
QInfiniteScroll internally tracks its loading state (e.g., isFetching, done).
When the list is replaced or updated (e.g., due to a filter or sort change),
the internal state does NOT reset automatically.
Workaround:
Use a "reset trigger" from the parent via a reactive prop (resetKey)
and then reset QInfiniteScroll

Height:
By default the QScrollArea fills its parent, which requires a parent with a fixed height. For lists that
are embedded in a flowing layout - e.g. inside a card - pass `maxHeight` instead: the content is then
measured and the list grows with it until it reaches that height. This detour is necessary because a
QScrollArea is size contained (`contain: strict`) and can never derive its height from its content.
-->
<script setup lang="ts">
import { QInfiniteScroll, QScrollArea } from "quasar";
import { computed, ref, watch } from "vue";
import { useElementSize } from "@vueuse/core";

const props = defineProps<{
    /** Height (in px) at which the list stops growing and starts scrolling. Omit to fill the parent. */
    maxHeight?: number;
    resetKey?: number;
    loadMoreText?: string;
}>();

const emit = defineEmits<{
    loadMore: [done: (stop?: boolean | undefined) => void];
}>();

const infiniteScrollRef = ref<InstanceType<typeof QInfiniteScroll> | null>(null);
const scrollAreaRef = ref<InstanceType<typeof QScrollArea> | null>(null);

const fireLoadMore = (_index: number, done: (stop?: boolean | undefined) => void) => {
    emit("loadMore", done);
};

/*
The QInfiniteScroll element is measured rather than the slot content, so that the loading spinner is part
of the height and the list does not jump while a page is loading. Measuring the QScrollArea's own content
wrapper would not work: it carries `min-height: 100%` and would always report the current height back.
Without a `maxHeight` nothing has to be measured at all - the scroll area fills its parent then, and no
ResizeObserver is created for the many lists that use this default.
*/
const infiniteScrollElement = computed<HTMLElement | undefined>(() =>
    props.maxHeight === undefined ? undefined : infiniteScrollRef.value?.$el
);
const { height: contentHeight } = useElementSize(infiniteScrollElement);

const scrollAreaHeight = computed(() => {
    // until the content is measured the full height is used, so that the infinite scroll polls for a first page
    if (contentHeight.value === 0) return props.maxHeight;

    return Math.min(contentHeight.value, props.maxHeight ?? Infinity);
});

watch(
    () => props.resetKey,
    async () => {
        /*
        Resetting immediately or with nextTick may run too early if the component
        is conditionally rendered or inside QScrollArea. Using setTimeout ensures
        the DOM and scrollTarget are ready before calling reset() and resume()
        */
        setTimeout(() => {
            infiniteScrollRef.value?.reset();
            infiniteScrollRef.value?.resume();
            scrollAreaRef.value?.setScrollPosition("vertical", 0);
        }, 200);
    }
);
</script>

<style lang="scss" scoped>
.loading {
    .q-infinite-scroll__loading.invisible & {
        display: none;
    }
}
</style>
